import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Risk › the escalation queue (`rk-cases`), from `GET /v1/admin/aml/escalations`.
 *
 * # What this is, and why it is not `rk-aml`
 *
 * `rk-aml` reads `GET /aml/cases` unfiltered — every case, escalated or not
 * (#184). This is the other population: the cases that were **sent to the
 * institution**, carrying the institution's acknowledgement and outcome.
 *
 * The endpoint existed and nothing read it. `rk-cases` was going to be wired to
 * `/aml/cases` and would have been a second screen over `rk-aml`'s read; this
 * is the screen that id should always have been.
 *
 * The rail says "Cases" and this screen says what it shows. `NAV` is the design
 * contract and is not edited — the same arrangement `rk-aml` and `sf-failed`
 * already have.
 *
 * # Read only, and the controls are not missing — they are elsewhere
 *
 * Acknowledging an escalation and recording the institution's outcome are
 * `POST /aml/cases/{ref}/acknowledge` and `/resolve`. They hang off the CASE
 * and are already reachable from the AML queue's row opener. A second set here
 * would be the duplicate #184 exists to stop.
 *
 * # `posture` without `clock` is half an answer
 *
 * Posture is `breached | due_soon | on_track | settled`, and the clock says
 * **which deadline produced it**:
 *
 * > *"an escalation 40 days old that was never acknowledged is chased by asking
 * > whether they received it, not by asking what they decided."*
 *
 * Both chases read as "breached" and they are different phone calls, so the
 * clock is rendered beside the posture rather than behind it.
 *
 * # Overdue and remaining are two fields, and that is deliberate
 *
 * > *"'overdue by three days' and 'due in three days' must not be renderable
 * > from the same value with a wrong sign."*
 *
 * So this module never subtracts one from the other and never negates either.
 * Whichever is served is the one rendered.
 *
 * # `since_raised` is NOT how overdue it is
 *
 * How long the institution has had it, and how far past a deadline it is, are
 * different questions — the deadlines are pinned per row. Both are shown, in
 * separate columns, and neither is derived from the other.
 *
 * # The counts are populations, not the page
 *
 * `breached` and `open` count the whole set. Deriving them from the rows would
 * produce *"a screen showing '3 breached' because three fitted on the page —
 * one that gets quieter as the problem gets worse."*
 *
 * # Escalations that cannot be timed are counted, not hidden
 *
 * `unrecorded_escalations` are cases reading `escalated` with no package on
 * record, from before this capability existed. They have no clock and cannot
 * appear as rows, and *"a queue that quietly covers less than the table it sits
 * on is worse than one that says so."*
 */

export const AML_ESCALATIONS_PATH = '/aml/escalations'

const SOURCE = 'postgres · aml_escalations (GET /v1/admin/aml/escalations)'
const AUTHORITY = 'operational'

interface Escalation {
  readonly escalationRef: string
  readonly caseRef: string
  readonly customerId: number | null
  readonly reasonCode: string
  /** `raised | acknowledged | resolved`. There is no `filed`. */
  readonly status: string
  readonly posture: string
  /** Which deadline produced the posture. Read WITH it, never instead of it. */
  readonly clock: string
  readonly raisedAt: string
  readonly raisedBy: string
  readonly dueAt: string
  /** Exactly one of these two is meaningful. Never combined, never negated. */
  readonly overdueSeconds: number | null
  readonly remainingSeconds: number | null
  /** How long they have had it. NOT how overdue it is. */
  readonly sinceRaisedSeconds: number | null
  readonly acknowledgedAt: string
  readonly institutionRef: string
  readonly resolvedAt: string
  readonly outcome: string
  readonly blocking: string
  readonly needsAHuman: boolean
}

function readEscalation(raw: unknown): Escalation | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const escalationRef = str(o['escalation_ref'])
  if (escalationRef === '') return null
  return {
    escalationRef,
    caseRef: str(o['case_ref']),
    customerId: int(o['customer_id']),
    reasonCode: str(o['reason_code']),
    status: str(o['status']),
    posture: str(o['posture']),
    clock: str(o['clock']),
    raisedAt: str(o['raised_at']),
    raisedBy: str(o['raised_by']),
    dueAt: str(o['due_at']),
    overdueSeconds: int(o['overdue_seconds']),
    remainingSeconds: int(o['remaining_seconds']),
    sinceRaisedSeconds: int(o['since_raised_seconds']),
    acknowledgedAt: str(o['acknowledged_at']),
    institutionRef: str(o['institution_ref']),
    resolvedAt: str(o['resolved_at']),
    outcome: str(o['institution_outcome']),
    blocking: str(o['blocking']),
    needsAHuman: o['needs_a_human'] === true,
  }
}

const COLS: readonly Column[] = [
  ['Escalation', 'm', 'minmax(150px,1.1fr)'],
  ['Case', 'm', '130px'],
  ['Customer', 'm', '110px'],
  ['Why', 'x', 'minmax(150px,1.1fr)'],
  ['Where it is', 'c', '160px'],
  ['Against which clock', 'c', 'minmax(200px,1.4fr)'],
  ['They have had it', 'm', '140px'],
  ['Institution', 'x', 'minmax(160px,1.2fr)'],
  ['Waiting on', 'x', 'minmax(150px,1.1fr)'],
]

function statusCell(e: Escalation): ChipCell {
  switch (e.status) {
    case '': return ['— not stated', 'r']
    case 'raised': return ['raised — not yet acknowledged', 'a']
    case 'acknowledged': return ['acknowledged', 'n']
    case 'resolved': return ['resolved', 'g']
    default: return [e.status, 'n']
  }
}

/**
 * The posture and the clock it was measured against, in one cell.
 *
 * Never the posture alone. Two escalations both reading `breached` need
 * different phone calls — one asks whether the institution received it, the
 * other asks what they decided — and only the clock tells them apart.
 *
 * The overdue and remaining figures are rendered from whichever field was
 * served. They are never combined into one signed number, because that is the
 * shape in which "overdue by three days" and "due in three days" become the
 * same rendering with a sign error.
 */
function postureCell(e: Escalation): ChipCell {
  const clock = e.clock === '' ? 'an unnamed clock' : e.clock
  const by = e.overdueSeconds !== null
    ? ` · ${days(e.overdueSeconds)} over`
    : e.remainingSeconds !== null
      ? ` · ${days(e.remainingSeconds)} left`
      : ''

  switch (e.posture) {
    case '': return ['— not stated', 'r']
    case 'breached': return [`BREACHED on ${clock}${by}`, 'r']
    case 'due_soon': return [`due soon on ${clock}${by}`, 'a']
    case 'on_track': return [`on track on ${clock}${by}`, 'g']
    case 'settled': return ['settled — no clock runs', 'n']
    default: return [`${e.posture} · ${clock}`, 'n']
  }
}

/** What the institution has said, which is a different axis from the clock. */
function institutionCell(e: Escalation): Cell {
  if (e.outcome !== '') {
    return `${e.outcome}${e.resolvedAt === '' ? '' : ` · ${day(e.resolvedAt)}`}`
  }
  if (e.acknowledgedAt !== '') {
    return `acknowledged ${day(e.acknowledgedAt)}${e.institutionRef === '' ? ' · NO REFERENCE' : ` · ${e.institutionRef}`}`
  }
  return '— they have not acknowledged it'
}

function row(e: Escalation): readonly Cell[] {
  return [
    e.escalationRef,
    e.caseRef === '' ? '— not stated' : e.caseRef,
    e.customerId === null ? '— not stated' : String(e.customerId),
    e.reasonCode === '' ? '— not stated' : e.reasonCode,
    statusCell(e),
    postureCell(e),
    // Independent of the posture: how long they have had it is not how far past
    // a deadline it is, and the deadlines are pinned per row.
    e.sinceRaisedSeconds === null ? '—' : days(e.sinceRaisedSeconds),
    institutionCell(e),
    e.blocking === '' ? '—' : e.blocking,
  ]
}

export function composeEscalations(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['escalations'])
    ? (payload['escalations'] as readonly unknown[])
    : []
  const rows: Escalation[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readEscalation(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  // SERVED populations. Never counted from the rows — a page-derived count gets
  // quieter as the problem gets worse.
  const breached = int(payload['breached'])
  const open = int(payload['open'])
  const unrecorded = int(payload['unrecorded_escalations'])
  const needHuman = rows.filter((e) => e.needsAHuman).length

  const table: TableBlock = {
    t: 'table',
    title: 'Escalations to the institution',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} on this page${open === null ? '' : ` · ${String(open)} open in total`}${breached === null ? '' : ` · ${String(breached)} breached in total`}${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'The totals are the whole population, not this page — a queue that reported three breached because three fitted on the page would get quieter as the problem got worse. Read the posture WITH the clock beside it: an escalation nobody acknowledged and one nobody decided are both breaches, and they are different phone calls.',
  }

  const blocks: Block[] = [table]

  if (breached !== null && breached > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Breached',
      title: `${String(breached)} escalation(s) are past a deadline`,
      body: 'The clock named on each row says which deadline, and that decides what to do about it. One that was never acknowledged is chased by asking the institution whether they received it; one acknowledged and undecided is chased by asking what they decided. The count is the whole population rather than what fitted on this page.',
      ref: 'breached is served on the response',
    })
  }

  if (unrecorded !== null && unrecorded > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not on this queue and not forgotten',
      title: `${String(unrecorded)} case(s) read as escalated with no package on record`,
      body: 'They were escalated before this capability existed, so there is nothing to time them against and they cannot appear as rows here. They are counted rather than omitted: a queue that quietly covers less than the table it sits on is worse than one that says so. Find them on the AML case queue.',
      ref: 'unrecorded_escalations is served, and is not a row count',
    })
  }

  if (needHuman > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Waiting on us',
      title: `${String(needHuman)} escalation(s) are blocked on somebody here, not on the institution`,
      body: 'The backend says so per row rather than leaving it to be inferred from the status. An escalation waiting on us and one waiting on them look similar on a queue and are opposite problems — only one of them is chased by making a phone call.',
      ref: 'needs_a_human is served per row',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Empty',
      title: 'No escalation is on this queue',
      body: `That means no AML case has been sent to the institution within this read${unrecorded !== null && unrecorded > 0 ? ' — apart from the unrecorded ones counted above, which cannot appear here' : ''}. It is not a statement that no case needs escalating: whether one should be raised is a question about the case queue, not about this one.`,
      ref: 'an empty escalation queue is not an empty case queue',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} escalation row(s) could not be read`,
      body: 'Counted rather than dropped silently. A row without an escalation reference cannot be matched to what the institution was sent, and a breach queue one row shorter than the record understates the problem — which is the direction that matters here.',
      ref: 'the count is the finding',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (open !== null && open > rows.length) {
    missing.push(`${String(open - rows.length)} open escalation(s) beyond this page`)
  }
  if (unrecorded !== null && unrecorded > 0) {
    missing.push(`${String(unrecorded)} escalation(s) with no package, which cannot be timed`)
  }
  // Always. The acts live on the case, and saying where is more use than a
  // disabled control naming a role that would not help.
  missing.push('acknowledging and resolving — those act on the case, from the AML case queue')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    title: 'Escalations to the institution',
    purpose:
      'AML cases that were sent to our e-money institution, with what they have said back. '
      + 'The rail calls this Cases; this is the escalated subset, and the whole case queue is AML cases.',
    subject: `aml_escalations · ${String(rows.length)} row(s) read`,
  }
}

export const loadEscalations: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(AML_ESCALATIONS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The escalation queue answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The escalation queue returned a shape this console does not know.', null)
  }
  return composeEscalations(payload as Record<string, unknown>, new Date())
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The escalation queue could not be read',
      body: `${reason} Nothing is drawn. This is NOT an empty queue: whether anything is sitting past a deadline with our e-money institution is unknown from here, and "nothing is breached" and "we could not find out" are the two answers that must never be confused on this screen.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}

/** Seconds as whole days, for a queue whose deadlines are in days. */
function days(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  if (d >= 1) return `${String(d)}d`
  const h = Math.floor(seconds / 3600)
  return h >= 1 ? `${String(h)}h` : `${String(Math.max(0, Math.floor(seconds / 60)))}m`
}
